import type {
  ReplayData,
  PlayerPosition,
  DeathEvent,
  PlayerRoundStats,
} from "@/lib/demo-parser";
import type { MapConfig } from "@/lib/map-config";
import { gameToRadarPoint, getMapRadarSize } from "@/lib/map-config";

/**
 * Determine if teams have switched sides for a given round number.
 * Regulation: 12 rounds per half. Overtime: MR3 (3 rounds per half).
 * OT continues from the second half's switched state (does NOT reset to original sides).
 *
 * Canonical implementation for replay UI helpers.
 */
export function areTeamsSwitched(roundNumber: number): boolean {
  if (roundNumber <= 12) return false;
  if (roundNumber <= 24) return true;
  const otRound = roundNumber - 24;
  const otHalf = Math.ceil(otRound / 3);
  return otHalf % 2 === 1;
}

/** Get team number (1 or 2) for a player by steamId. */
export function getPlayerTeam(
  steamId: string,
  team1PlayerIds: string[],
  team2PlayerIds: string[]
): 1 | 2 | null {
  if (team1PlayerIds.includes(steamId)) return 1;
  if (team2PlayerIds.includes(steamId)) return 2;
  return null;
}

/** Get player name from a players array. */
export function getPlayerName(
  steamId: string,
  players: PlayerPosition[]
): string {
  return players.find((p) => p.steamId === steamId)?.name || "Unknown";
}

/**
 * Get the CSS color class for a player's current side.
 * Team 1 starts CT (blue). When switched, Team 1 is T (orange).
 */
export function getPlayerSideClass(
  steamId: string,
  roundNumber: number,
  team1PlayerIds: string[],
  team2PlayerIds: string[]
): string {
  const playerTeam = getPlayerTeam(steamId, team1PlayerIds, team2PlayerIds);
  if (!playerTeam) return "text-neutral-400";
  const switched = areTeamsSwitched(roundNumber);
  if (playerTeam === 1) {
    return switched ? "text-orange-400" : "text-blue-400";
  }
  return switched ? "text-blue-400" : "text-orange-400";
}

/** Filter deaths for current round's kill feed. */
export function getKillFeed(
  deaths: DeathEvent[],
  roundStartTick: number,
  currentTick: number
): DeathEvent[] {
  return deaths.filter(
    (d) => d.tick >= roundStartTick && d.tick <= currentTick
  );
}

/** Calculate cumulative stats for all players up to current tick. */
export function calculateRoundStats(
  replayData: ReplayData,
  currentFrame: number,
  currentTick: number
): PlayerRoundStats[] {
  const statsMap = new Map<string, PlayerRoundStats>();

  const frame = replayData.frames[currentFrame];
  if (frame) {
    frame.players.forEach((p) => {
      const team = getPlayerTeam(
        p.steamId,
        replayData.team1PlayerIds,
        replayData.team2PlayerIds
      );
      if (team) {
        statsMap.set(p.steamId, {
          steamId: p.steamId,
          name: p.name,
          team,
          kills: 0,
          deaths: 0,
          assists: 0,
        });
      }
    });
  }

  const firstRoundStartTick = replayData.roundStartTicks[0] || 0;
  const allDeathsUpToCurrent = replayData.deaths.filter(
    (d) => d.tick >= firstRoundStartTick && d.tick <= currentTick
  );

  for (const death of allDeathsUpToCurrent) {
    if (statsMap.has(death.victimSteamId)) {
      statsMap.get(death.victimSteamId)!.deaths++;
    }
    if (
      death.attackerSteamId &&
      death.attackerSteamId !== death.victimSteamId &&
      statsMap.has(death.attackerSteamId)
    ) {
      statsMap.get(death.attackerSteamId)!.kills++;
    }
  }

  return Array.from(statsMap.values());
}

/** Create coordinate transformation functions for mapping game coords to screen. */
export function createCoordinateTransform(
  canvasWidth: number,
  canvasHeight: number,
  mapConfig: MapConfig | null,
  fallbackPlayers?: PlayerPosition[]
): { toScreenX: (x: number) => number; toScreenY: (y: number) => number } {
  if (mapConfig) {
    const radarSize = getMapRadarSize(mapConfig);
    const padding = 50;
    const availableWidth = canvasWidth - padding * 2;
    const availableHeight = canvasHeight - padding * 2;
    const renderScale = Math.min(
      availableWidth / radarSize,
      availableHeight / radarSize
    );

    return {
      toScreenX: (x: number) => {
        return padding + gameToRadarPoint(mapConfig, x, 0).x * renderScale;
      },
      toScreenY: (y: number) => {
        return padding + gameToRadarPoint(mapConfig, 0, y).y * renderScale;
      },
    };
  }

  // Fallback: dynamic bounds based on player positions
  const players = fallbackPlayers || [];
  const allX = players.map((p) => p.x);
  const allY = players.map((p) => p.y);
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);

  const padding = 100;
  const mapWidth = maxX - minX || 1000;
  const mapHeight = maxY - minY || 1000;
  const scale = Math.min(
    (canvasWidth - padding * 2) / mapWidth,
    (canvasHeight - padding * 2) / mapHeight
  );

  return {
    toScreenX: (x: number) => padding + (x - minX) * scale,
    toScreenY: (y: number) => padding + (y - minY) * scale,
  };
}
